import { z } from "zod";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { defineRoute } from "../http/route.js";
const Tenant = { surface: "tenant", auth: "user", marks: ["tenant"] };
export const Department = z
    .object({
    id: Id,
    parentId: Id.nullable(),
    name: LocalizedText,
    /** Ancestors from the root down, excluding the department itself. */
    ancestors: z.array(Id),
    sortOrder: z.number().int(),
    memberCount: z.number().int().nonnegative().describe("Active members directly in this department"),
})
    .meta({ id: "Department" });
export const Position = z
    .object({ id: Id, name: LocalizedText, memberCount: z.number().int().nonnegative() })
    .meta({ id: "Position" });
const DepartmentId = z.object({ departmentId: Id });
const PositionId = z.object({ positionId: Id });
const SortOrder = z.number().int().min(0).max(10_000);
export const listDepartments = defineRoute({
    ...Tenant,
    id: "directory.departments.list",
    method: "GET",
    path: "/v1/departments",
    permission: "directory:read",
    summary: "The department tree of the active enterprise, parents before children",
    prd: ["DIR-1"],
    responses: { 200: { description: "Departments", schema: z.object({ items: z.array(Department) }) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const createDepartment = defineRoute({
    ...Tenant,
    id: "directory.departments.create",
    method: "POST",
    path: "/v1/departments",
    permission: "department:manage",
    summary: "Add a department at the top or under a parent",
    prd: ["DIR-1"],
    request: {
        body: z.object({
            name: LocalizedText,
            parentId: Id.nullable().default(null),
            sortOrder: SortOrder.default(0),
        }),
    },
    responses: { 201: { description: "The department", schema: Department } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const updateDepartment = defineRoute({
    ...Tenant,
    id: "directory.departments.update",
    method: "PATCH",
    path: "/v1/departments/{departmentId}",
    permission: "department:manage",
    summary: "Rename, reorder, or move a department with its whole subtree; a move under its own subtree is refused",
    prd: ["DIR-1"],
    request: {
        params: DepartmentId,
        body: z.object({
            name: LocalizedText.optional(),
            parentId: Id.nullable().optional(),
            sortOrder: SortOrder.optional(),
        }),
    },
    responses: { 200: { description: "The department", schema: Department } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const deleteDepartment = defineRoute({
    ...Tenant,
    id: "directory.departments.delete",
    method: "DELETE",
    path: "/v1/departments/{departmentId}",
    permission: "department:manage",
    summary: "Delete a department with no sub-departments and no members",
    prd: ["DIR-1"],
    request: { params: DepartmentId },
    responses: { 204: { description: "Deleted" } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const listPositions = defineRoute({
    ...Tenant,
    id: "directory.positions.list",
    method: "GET",
    path: "/v1/positions",
    permission: "directory:read",
    summary: "Positions of the active enterprise",
    prd: ["DIR-1"],
    responses: { 200: { description: "Positions", schema: z.object({ items: z.array(Position) }) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const createPosition = defineRoute({
    ...Tenant,
    id: "directory.positions.create",
    method: "POST",
    path: "/v1/positions",
    permission: "department:manage",
    summary: "Add a position",
    prd: ["DIR-1"],
    request: { body: z.object({ name: LocalizedText }) },
    responses: { 201: { description: "The position", schema: Position } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const updatePosition = defineRoute({
    ...Tenant,
    id: "directory.positions.update",
    method: "PATCH",
    path: "/v1/positions/{positionId}",
    permission: "department:manage",
    summary: "Rename a position",
    prd: ["DIR-1"],
    request: { params: PositionId, body: z.object({ name: LocalizedText }) },
    responses: { 200: { description: "The position", schema: Position } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const deletePosition = defineRoute({
    ...Tenant,
    id: "directory.positions.delete",
    method: "DELETE",
    path: "/v1/positions/{positionId}",
    permission: "department:manage",
    summary: "Delete a position nobody holds",
    prd: ["DIR-1"],
    request: { params: PositionId },
    responses: { 204: { description: "Deleted" } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const DirectoryEntry = z
    .object({
    membershipId: Id,
    userId: Id,
    displayName: z.string(),
    avatarFileId: Id.nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable().describe("Shown only when the person chose to show it to colleagues"),
    department: z.object({ id: Id, name: LocalizedText }).nullable(),
    position: z.object({ id: Id, name: LocalizedText }).nullable(),
})
    .meta({ id: "DirectoryEntry" });
export const listDirectory = defineRoute({
    ...Tenant,
    id: "directory.members.list",
    method: "GET",
    path: "/v1/directory",
    permission: "directory:read",
    summary: "Active colleagues in the active enterprise, by name; a department filter includes its sub-departments. Never shows other enterprises",
    prd: ["DIR-1", "DIR-4"],
    request: {
        query: z.object({
            departmentId: Id.optional(),
            q: z.string().trim().min(1).max(100).optional(),
            limit: z.coerce.number().int().min(1).max(200).default(100),
            cursor: z.string().regex(/^\d+$/).optional().describe("Opaque; pass nextCursor back"),
        }),
    },
    responses: {
        200: {
            description: "Colleagues",
            schema: z.object({ items: z.array(DirectoryEntry), nextCursor: z.string().nullable() }),
        },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const directoryRoutes = [
    listDepartments,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    listPositions,
    createPosition,
    updatePosition,
    deletePosition,
    listDirectory,
];
//# sourceMappingURL=directory.routes.js.map